import type { BranchFilters } from "@/api"
import http from "@/http/http"
import type { IndustryDataRange } from "./enum"
import type { ProfileBasicItem } from "./interface"

export interface District {
  id: string
  name: string
}

export interface BrandVO {
  poiId: string
  storeName: string
  address: string
  latitude: number
  longitude: number
  district: District
}

export type StoreProfileArgs = {
  /**
   * poi id
   */
  poiId?: string
  /**
   * 城市id
   */
  cityId: string
  /**
   * 品牌id
   */
  brandId: string
  /**
   * 模块名称
   */
  moduleType: string
  /**
   * 纬度
   */

  latitude?: number
  /**
   * 经度
   */
  longitude?: number
}

export interface StoreOutlineProfile {
  // 品牌名称
  brandName: string
  // 品牌门店网点数
  brandStoreCount: number
  // 城市品牌门店网点数
  industryStoreCount: number
  // 网点个数排名
  storeRanking: number
  // TGI指数
  tgiRatio: number
  // 品牌覆盖人数（品牌在城市所有网点在150m半径覆盖到的到访人口的并集）
  coveredCount: number
  // 常驻人口
  residentCount: number
  // 年龄分布
  age: ProfileBasicItem[]
  // 消费能力
  consumption: ProfileBasicItem[]
  // 住房等级指数
  housePrice: ProfileBasicItem[]
  // 有车指数
  carAssets: ProfileBasicItem[]
  // 年龄分布 - 城市
  cityAge: ProfileBasicItem[]
  // 消费能力 - 城市
  cityConsumption: ProfileBasicItem[]
  // 住房等级指数 - 城市
  cityHousePrice: ProfileBasicItem[]
  // 有车指数 - 城市
  cityCarAssets: ProfileBasicItem[]
}

/**
 * @name 门店概况画像
 *
 * @export
 * @param {StoreProfileArgs} params
 * @returns
 */
export async function getStoreOutlineProfile(params: StoreProfileArgs) {
  try {
    try {
      const result = await http.post<StoreOutlineProfile>(
        "/v1/industry/storeOutlineProfile",
        params,
      )
      return result?.data
    } catch (error) {
      throw error
    }
  } catch (error) {}
}

interface StoreAreaProfileArgs {
  // 品牌id
  brandId: string
  // 城市id
  cityId: string
  // 区县ID
  districtId?: string
}

// 区域分析
interface AreaAnalysisVO {
  // 区域编码
  areaCode: string
  // 网点数
  siteCount: number
  // 区域同业网点数
  competitionCount: number
  // 区域同业门店数
  competitionRank: string
}

// 区域分布
interface AreaDistributionItemVO {
  // 区域名称
  areaName: string
  // 区域编码
  areaCode: string
  // 网点数
  siteCount: number
}

// 网点诊断
interface AreaDiagnoseVO {
  // 区域编码
  areaCode: string
  // 金牌网点数
  highQualityCount: number
  // 潜力网点数
  lowQualityCount: number
}

export interface AreaProfileVO {
  // 区域分析
  areaAnalysis: AreaAnalysisVO
  // 区域分布
  areaDistributionItems: AreaDistributionItemVO[]
  // 网点诊断
  areaDiagnose: AreaDiagnoseVO
}

/**
 * @name 区域网点分析
 *
 * @export
 * @param {StoreAreaProfileArgs} params
 * @returns
 */
export async function getStoreAreaProfile(params: StoreAreaProfileArgs) {
  try {
    try {
      const result = await http.post<AreaProfileVO>("/pointAnalysis/profile/area", params)
      return result.data
    } catch (error) {
      throw error
    }
  } catch (error) {}
}

export type StoreRankingArgs = {
  /**
   * 城市id
   */
  cityId: string
  /**
   * 品牌id
   */
  brandId: string
  /**
   * 模块名称
   */
  moduleType?: string
  /**
   * 地图比例尺(单位:米)，例：1:2公里，传参：2000
   */
  scale: number
  /**
   * true-升序，false-降序
   */
  isAsc: boolean
  /**
   * 页码
   */
  page: number
  /**
   * 每页数量
   */
  limit: number
  /**
   * 层级关系
   * */
  outletscode?: string
  /**
   * 网点类型
   * */
  outletstype?: string
  /**
   * 网点定位
   * */
  outletscategory?: string
}
// 网点
export interface StoreRanking {
  // poi id
  poiId: string
  // 排行
  ranking: number
  // 网点名称(支行)
  storeName: string
  poiName: string
  // 城市
  cityName: string
  // 区县
  district: any
  districtCode: string
  districtName: string
  // 街道
  township: { id: string; name: string }
  // 辐射半径
  coveredRange: number
  // 纬度
  latitude: number
  // 经度
  longitude: number
  // 客流评分
  flowRatio: number
  // 选址分值
  rankingScore: string
  // 地址
  address: string
  // 此网点是否开通/解锁, false-未开通，true-已开通
  unlocked: boolean
  // aoi
  aoiGeometry: number[][]
  /**
   * 层级关系
   * */
  outletscode: string
  /**
   * 网点类型
   * */
  outletstype: string
  /**
   * 网点定位
   * */
  outletscategory: string
  // 员工数
  employeeCount?: number
  districtId: string
  // 自定义添加属性
  type?: string
}

/**
 * @name 网点数排名
 *
 * @export
 * @param {StoreRankingArgs} data
 * @returns {StoreRanking}
 */
export async function getStoreList(data: StoreRankingArgs) {
  try {
    const result = await http.post<StoreRanking[]>(`/business/poi/list`, data)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface HourBasicItem {
  /**
   * 小时
   */
  hour: string
  /**
   * 人数
   */
  count: number
}
export interface StoreProfile {
  // 名称
  brandName: string
  // 网点名称
  storeName: string
  // 地址
  address: string
  // 数据时间
  dataDate: string
  // 网点类型
  storeType: string[]
  // 上月网点辐射人数
  lastMonthCoveredCount: number
  // 到访分时客流
  visitHourRatio: HourBasicItem[]
  // 老龄顾客到访分时客流
  elderVisitHourRatio: HourBasicItem[]
  // 年龄分布
  age: ProfileBasicItem
  // 消费能力
  consumption: ProfileBasicItem
  // 住房等级指数
  housePrice: ProfileBasicItem
  // 有车指数
  carAssets: ProfileBasicItem
  // 年龄分布 - 本行平均
  avgAge: ProfileBasicItem
  // 消费能力 - 本行平均
  avgConsumption: ProfileBasicItem
  // 住房等级指数 - 本行平均
  avgHousePrice: ProfileBasicItem
  // 有车指数 - 本行平均
  avgCarAssets: ProfileBasicItem
}

/**
 * @name 门店详细画像
 *
 * @export
 * @param {StoreProfileArgs} params
 * @returns StoreProfile
 */
export async function getStoreProfile(params: StoreProfileArgs) {
  try {
    const result = await http.post<StoreProfile>("/pointAnalysis/profile/poi", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

/**
 * 按区统计
 * @param cityId
 * @param brandId
 * @returns
 */
export type TStoreCounts = {
  id: string
  name: string
  count: number
}
export async function getStoreCount(cityId: string, brandId: string): Promise<TStoreCounts[]> {
  try {
    const result = await http.post<TStoreCounts[]>("/v1/industry/statCountByDistrict", {
      cityId,
      brandId,
      moduleType: "WANG_DIAN",
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

/**
 * 获取某个城市的区县列表
 */
export interface DistrictArgs {
  id: string
  name: string
  // 本地添加的
  text: string
  value: string
}
export async function getCityDistrict(cityId: string) {
  try {
    const result = await http.get<DistrictArgs>(`/v1/industry/cityDistrict/${cityId}`)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface UnlockSingleStoreArgs {
  cityId: string
  brandId: string
  poiId: string
  // moduleType: string
  dataRange?: IndustryDataRange
}

/**
 * 解锁某个网点权限api
 *
 * @export {boolean}
 * @param {UnlockSingleStoreArgs} params
 * @returns
 */
export async function unlockSingleStore(params: UnlockSingleStoreArgs) {
  try {
    const result = await http.post<boolean>(`/v1/industrySingleAuth/unlockSingleStore`, {
      ...params,
    })
    return result.data
  } catch (error) {
    throw error
  }
}

export interface SurplusQuotaArgs {
  cityId: string
  brandId: string
  dataRange?: IndustryDataRange | boolean
}

/**
 * 获取剩余的网点权限额度
 *
 * @export
 * @param {SurplusQuotaArgs} params
 * @returns
 */
export async function getSurplusQuota(params: SurplusQuotaArgs) {
  // return 10
  try {
    const result = await http.get<number>(`/v1/industrySingleAuth/surplusQuota`, { params })
    return result.data
  } catch (error) {
    throw error
  }
}

interface BranchCountResult {
  // 品牌未开通网点数
  lockedCount: number
  // 品牌已开通网点数
  unlockedCount: number
}

/**
 * 品牌在所有城市的未开通和已开通总数
 */
export async function getBrandStoreCount(brandId: string) {
  try {
    const result = await http.get<BranchCountResult>(`/business/poi/count/${brandId}`)
    return result?.data
  } catch (error) {
    throw error
  }
}

export type AreaCoutArgs = {
  brandId: string
  hasAuth?: boolean
} & BranchFilters

export interface AreaCoutTreeItem {
  id: string
  // PROVINCE, CITY, DISTRICT
  level: string
  poiCount: number
  employeeCount: number
  children?: AreaCoutTreeItem[]
}

/**
 * 获取省市区网点热力
 */
export async function getAreaCount(params: AreaCoutArgs) {
  try {
    const result = await http.get<AreaCoutTreeItem[]>(`/business/poi/areaCount/${params.brandId}`)
    return result.data
  } catch (error) {
    throw error
  }
}
